import React, { useRef } from 'react';
import { useParams } from 'react-router-dom';

import Heading from 'components/partials/heading/heading';
import Breadcrumbs from 'components/partials/beadcrumbs/breadcrumbs';
import ActionMenu from 'components/partials/action-menu/action-menu';
import Loader from 'components/partials/loader/loader';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';

import { useInsightDetails } from 'containers/user/insights/insight-detail/hooks/use-insight-details';
import { useInsightComments } from 'containers/user/insights/insight-detail/hooks/use-insight-comments';
import { useExportPdf } from 'containers/admin/clients/client/tabs-components/insights-tab/insight-details/hooks/use-export-pdf';
import InsightDetailsTab from 'containers/admin/clients/client/tabs-components/insights-tab/insight-details/tabs/insight-details-tab';
import PdfContent from 'containers/admin/clients/client/tabs-components/insights-tab/insight-details/components/pdf-content';

import { useCurrentUser } from 'state/current-user/current-user.thunk';

import { formatDate } from 'helpers/dates-formatter';

import Icons from 'assets/icons';

const InsightDetail = (): React.JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const pdfContentRef = useRef<HTMLDivElement>(null);

  const currentUser = useCurrentUser();
  const { insight, loading, error, markInsightDownloaded } = useInsightDetails({
    insightId: id,
  });

  const { comments, loading: commentsLoading } = useInsightComments({
    insightId: id,
  });

  const { exportToPdf, isExporting } = useExportPdf();

  const userId = currentUser?.data?.oktaUserId;
  const currentUserId = currentUser?.data?.id;

  if (loading) {
    return <Loader loading={true} />;
  }

  if (error || !insight) {
    return (
      <ResponsiveWrapper>
        <div className="text-center py-8">
          <p className="text-red-500">Error loading insight details</p>
        </div>
      </ResponsiveWrapper>
    );
  }

  const breadcrumbs = [
    { label: 'Insights', href: '/insights' },
    { label: insight.name ?? 'Insight', isActive: true },
  ];

  const handleDownloadPdf = async (): Promise<void> => {
    try {
      await exportToPdf({
        insightName: insight.name ?? 'Insight',
        contentRef: pdfContentRef,
      });

      if (userId && currentUserId && id && !insight.downloaded) {
        await markInsightDownloaded();
      }
    } catch (err) {
      console.error('Error exporting PDF:', err);
    }
  };

  return (
    <ResponsiveWrapper>
      <Breadcrumbs breadcrumbs={breadcrumbs} />

      <div className="flex justify-between items-start mt-6 mb-5 gap-x-8">
        <div>
          <Heading variant="h1" customClass="text-3xl mb-1.5 block">
            {insight.name}
          </Heading>
          <div className="flex items-center text-gray-700 mb-4 gap-x-3">
            <div className="flex items-center gap-1">
              <Icons.Calendar className="size-4" />
              <div className="text-sm font-semibold">
                {formatDate(insight.createdDate, 'MMM D, YYYY')}
              </div>
            </div>
            <div>
              <span className="text-gray-600 text-sm">Created by: </span>
              <span className="text-gray-700 text-sm font-semibold">
                {insight.createdBy || 'Unknown'}
              </span>
            </div>
          </div>
        </div>

        <ActionMenu
          buttonName="Download PDF"
          onEditClick={handleDownloadPdf}
          iconBefore={<Icons.Download />}
          isActionMenuDisabled={isExporting}
          loading={isExporting}
        />
      </div>

      <InsightDetailsTab
        insight={insight}
        comments={comments}
        commentsLoading={commentsLoading}
        isAdminView={false}
      />

      {/* Hidden PDF content for download */}
      <div
        className="fixed top-[-9999px] left-[-9999px] opacity-0 pointer-events-none"
        aria-hidden="true"
      >
        <PdfContent
          ref={pdfContentRef}
          insight={insight}
          comments={comments}
          showViewReport={false}
        />
      </div>
    </ResponsiveWrapper>
  );
};

export default InsightDetail;
